import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePagination } from "../../../../../hooks/usePagination";
import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../../components/animate-appearence/AnimateAppearanceWrapper";
import { LoadingBlock } from "../../../../../components/loading-block/LoadingBlock";
import {
  Button,
  Dropdown,
  FilterTabs,
  PageTitle,
} from "../../../../../components/ui-kit";
import { useTableContext } from "../../../../../components/ui-kit/ReactTable/context/TableContext";
import { useTransactionsList } from "../hooks/useTransactionsList";
import { useTransactionsContext } from "../../context/TransactionsContextProvider";
import { TransactionsTable } from "../components/TransactionsTable";
import { transactionsSearchConfig } from "../config/SearchConfig";
import {
  TimeFilterValue,
  TIME_FILTER_OPTIONS,
  DEFAULT_TIME_FILTER,
  getDateFromTimeFilter,
  getTimeFilterFromDate,
} from "../utils/timeFilterUtils";
import api from "../../../../../util/api";
import { useAuthUserContext } from "../../../../../context/AdministratorContextProvider";
import { convertValueToIndexForFilterTabs } from "../../../../../util/data-utils";
import { AdvancedSearch } from "../../../../../components/advanced-search/AdvancedSearch";
import { SearchFieldType } from "../../../../../components/advanced-search/AdvancedSearch.interface";
import { OBtransactionsSearchConfig } from "../config/OBSearchConfig";
import { otherTransactionsSearchConfig } from "../config/OtherTransactionsSearchConfig";
import {
  PayableEntityType,
  PaymentProviders,
} from "@shared_packages/toolkit/types";

const MainContainer = styled.div<{ details?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ details }) => details && "margin-top: 30px;"}
`;

const TopRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TabsContainer = styled.div<{ details?: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  ${({ details }) =>
    !details &&
    `background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 20px 20px 30px;`}

  @media (max-width: 900px) {
    padding: ${({ details }) => (details ? "0" : "12px")};
  }
`;

const StyledTransactionsTable = styled(TransactionsTable)`
  padding-top: 30px;
`;

export enum TransactionsProviderTypes {
  OB = "ob",
  OTHER = "other",
}
interface TransactionsListProps {
  ownerId?: string;
  details?: boolean;
  className?: string;
  providerType?: TransactionsProviderTypes;
}

export const TransactionsList: React.FunctionComponent<
  TransactionsListProps
> = ({ ownerId, details, className, providerType }) => {
  const { authUser } = useAuthUserContext();
  const { sortConfig } = useTableContext();
  const [searchParams] = useSearchParams();
  const [csvLoading, setCsvLoading] = useState<boolean>(false);

  // Determine if we should use context or direct hook based on ownerId prop
  const useContextMode = !ownerId;

  // Context mode data
  const contextData = useTransactionsContext();
  const {
    transactionsData: contextTransactionsData,
    loading: contextLoading,
    totalDocs: contextTotalDocs,
    page: contextPage,
    limit: contextLimit,
    filters: contextFilters,
    onPaginationChange: contextOnPaginationChange,
    setTransactionsFilters: contextSetFilters,
    onSortChange: contextOnSortChange,
    setDirection: contextSetDirection,
    direction: contextDirection,
  } = contextData;

  // Direct hook mode data
  const [timeFilter, setTimeFilter] =
    useState<TimeFilterValue>(DEFAULT_TIME_FILTER);
  const [isUpdatingFromTimeFilter, setIsUpdatingFromTimeFilter] =
    useState(false);
  const previousFromDateRef = useRef<string | undefined>();
  const [selectedTimeFilterItem, setSelectedTimeFilterItem] = useState(() => {
    const defaultOption = TIME_FILTER_OPTIONS.find(
      (option) => option.value === DEFAULT_TIME_FILTER
    );
    return defaultOption || TIME_FILTER_OPTIONS[0];
  });
  const [dropdownKey, setDropdownKey] = useState(0);

  // Data based on mode
  const data = useContextMode
    ? {
        transactionsData: contextTransactionsData,
        totalDocs: contextTotalDocs,
        loading: contextLoading,
        refreshTransactions: () => undefined,
        page: contextPage,
        limit: contextLimit,
        onPaginationChange: contextOnPaginationChange,
        filters: contextFilters, // Use filters from context
        setFilters: contextSetFilters,
        sort: { field: "createdAt", direction: "desc" }, // Not used in context mode
        setSort: contextOnSortChange,
        direction: contextDirection,
        setDirection: contextSetDirection,
      }
    : (() => {
        const { page, limit, onPaginationChange, resetPageNum } =
          usePagination();
        const [filters, setFilters] = useState<Record<string, any>>(() => {
          const initialFromDate = getDateFromTimeFilter(DEFAULT_TIME_FILTER);
          return initialFromDate ? { from: initialFromDate.toISOString() } : {};
        });
        const [sort, setSort] = useState<any>({
          field: "createdAt",
          direction: "desc",
        });
        const [direction, setDirection] = useState<string | undefined>(
          searchParams.get("direction") ?? undefined
        );

        const hookResult = useTransactionsList(
          page,
          limit,
          direction,
          sort,
          filters,
          ownerId,
          undefined,
          providerType
        );

        // Same page-reset rules as TransactionsContextProvider (context mode).
        useEffect(() => {
          resetPageNum();
        }, [JSON.stringify(filters), direction, ownerId]);

        useEffect(() => {
          if (page > 1) {
            const maxPage = Math.ceil(hookResult.totalDocs / limit);
            if (maxPage > 0 && page > maxPage) {
              resetPageNum();
            }
          }
        }, [hookResult.totalDocs, page, limit]);

        return {
          ...hookResult,
          page,
          limit,
          onPaginationChange,
          filters,
          setFilters,
          sort,
          setSort,
          direction,
          setDirection,
        };
      })();

  const {
    transactionsData,
    totalDocs,
    loading,
    refreshTransactions,
    page,
    limit,
    onPaginationChange,
    filters,
    setFilters,
    sort,
    setSort,
    direction,
    setDirection,
  } = data;

  // Use appropriate data based on mode
  const currentTransactionsData = useContextMode
    ? contextTransactionsData
    : transactionsData;
  const currentLoading = useContextMode ? contextLoading : loading;
  const currentTotalDocs = useContextMode ? contextTotalDocs : totalDocs;
  const currentPage = useContextMode ? contextPage : page;
  const currentLimit = useContextMode ? contextLimit : limit;
  const currentOnPaginationChange = useContextMode
    ? contextOnPaginationChange
    : onPaginationChange;
  const currentSetFilters = useContextMode ? contextSetFilters : setFilters;
  const currentOnSortChange = useContextMode
    ? contextOnSortChange
    : (val: any) => setSort(val);
  const currentSetDirection = useContextMode
    ? contextSetDirection
    : setDirection;
  const currentDirection = useContextMode ? contextDirection : direction;

  useEffect(() => {
    if (sortConfig) {
      if (useContextMode) {
        contextOnSortChange(sortConfig);
      } else {
        if (
          sortConfig.field !== sort.field ||
          sortConfig.direction !== sort.direction
        ) {
          setSort(sortConfig);
        }
      }
    }
  }, [sortConfig, useContextMode, sort, setSort, contextOnSortChange]);

  // Initialize previousFromDateRef and timeFilter from the initial filters.
  // `from` may come back from the URL (Back / F5) as any window, or as ""
  // (explicitly "All time"), so the dropdown is derived from it rather than
  // assumed to be the default.
  useEffect(() => {
    previousFromDateRef.current = filters.from;
    const initialTimeFilter = getTimeFilterFromDate(filters.from || undefined);
    if (initialTimeFilter !== timeFilter) {
      setTimeFilter(initialTimeFilter);
    }
  }, []); // Only run once on mount

  // Update selectedTimeFilterItem when timeFilter changes
  useEffect(() => {
    const newSelectedItem =
      TIME_FILTER_OPTIONS.find((option) => option.value === timeFilter) ||
      TIME_FILTER_OPTIONS[0];
    setSelectedTimeFilterItem(newSelectedItem);
    setDropdownKey((prev) => prev + 1); // Force re-mount of Dropdown
  }, [timeFilter]);

  const onFiltersChange = (nextFilters: Record<string, any>) => {
    // Check if "from" date changed from datePicker (not from our time filter dropdown)
    if (
      !isUpdatingFromTimeFilter &&
      nextFilters.from !== previousFromDateRef.current
    ) {
      // Check if the date matches the default time filter to avoid incorrect updates
      const expectedFromDate = getDateFromTimeFilter(DEFAULT_TIME_FILTER);
      const isDefaultFilter =
        nextFilters.from &&
        expectedFromDate &&
        Math.abs(
          new Date(nextFilters.from).getTime() - expectedFromDate.getTime()
        ) < 300000; // Allow 5 minutes tolerance for timing differences

      if (isDefaultFilter) {
        // If it matches default, ensure timeFilter is set to DEFAULT_TIME_FILTER
        if (timeFilter !== DEFAULT_TIME_FILTER) {
          setTimeFilter(DEFAULT_TIME_FILTER);
        }
      } else {
        // Otherwise, use getTimeFilterFromDate to determine the correct filter
        const newTimeFilter = nextFilters.from
          ? getTimeFilterFromDate(nextFilters.from)
          : "all";
        if (newTimeFilter !== timeFilter) {
          setTimeFilter(newTimeFilter);
        }
      }
    }

    // Update the ref with current value before setting new filters
    previousFromDateRef.current = nextFilters.from;

    setFilters(nextFilters);
    currentSetFilters(nextFilters);
  };

  const onTimeFilterChange = (newTimeFilter: TimeFilterValue) => {
    setTimeFilter(newTimeFilter);
    setIsUpdatingFromTimeFilter(true);

    const fromDate = getDateFromTimeFilter(newTimeFilter);
    const newFilters = {
      ...filters,
      from: fromDate ? fromDate.toISOString() : undefined,
    };

    // Always update filters, regardless of mode
    setFilters(newFilters);
    currentSetFilters(newFilters);

    // Don't call onFiltersChange to avoid loops, just update filters directly
    previousFromDateRef.current = newFilters.from;

    // Reset flag after a short delay
    setTimeout(() => setIsUpdatingFromTimeFilter(false), 100);
  };

  // Prepare dropdown items for time filter
  const timeFilterItems = TIME_FILTER_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  // Export what the table shows: the live filters (incl. the Time range
  // dropdown, which bypasses AdvancedSearch) and the Incoming/Outgoing tab.
  const getCSV = async () => {
    setCsvLoading(true);
    // "Search by company" is a path param on the export endpoint, like in the
    // list hook — not a query param.
    const { companyId, ...csvFilters } = filters;
    try {
      const result = await api.getTransactionCSV(
        {
          ...csvFilters,
          ...(currentDirection ? { direction: currentDirection } : {}),

          ...(providerType
            ? providerType === TransactionsProviderTypes.OB
              ? {
                  paymentProvider: `${PaymentProviders.FENA_OB},${PaymentProviders.FENA_OB_QR},${PaymentProviders.FENA_MOBILE}`,
                }
              : {
                  paymentProvider: `${PaymentProviders.OTHER},${PaymentProviders.CASH}`,
                }
            : {}),
          ...(providerType
            ? {
                parentEntityType: PayableEntityType.PAYMENT,
              }
            : {}),
        },
        companyId ?? ownerId
      );
      const blob = await result.blob();
      const a = document.createElement("a");
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = `transactions.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } finally {
      setCsvLoading(false);
    }
  };

  const baseSearchConfig = providerType
    ? providerType === TransactionsProviderTypes.OB
      ? OBtransactionsSearchConfig
      : otherTransactionsSearchConfig
    : transactionsSearchConfig;

  const searchConfig = ownerId
    ? baseSearchConfig.filter(
        (field) => field.type !== SearchFieldType.SEARCH_BY_COMPANY_DROPDOWN
      )
    : baseSearchConfig;

  return (
    <AnimateAppearanceWrapper>
      <MainContainer className={className} details={details}>
        <TopRowContainer>
          <div style={{ flexDirection: "column", marginRight: "auto" }}>
            <PageTitle
              title={
                providerType
                  ? providerType === TransactionsProviderTypes.OB
                    ? "Open Banking payments"
                    : "Other payments"
                  : "Transactions"
              }
            />
          </div>
          {authUser.policies?.actionsOnPayments ? (
            <Button onClick={getCSV} loading={csvLoading}>
              Download
            </Button>
          ) : null}
        </TopRowContainer>
        <TabsContainer details={details}>
          <FilterTabs
            menus={tabsConfig}
            // Context mode writes the tab into the URL itself (merged with the
            // other params); a bare setSearchParams here would wipe them.
            clickHandler={(val) => currentSetDirection(val)}
            startWithIndex={convertValueToIndexForFilterTabs(
              tabsConfig,
              currentDirection
            )}
          />
          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px", fontWeight: 500 }}>
                Time range:
              </span>
              <Dropdown
                key={dropdownKey}
                items={timeFilterItems}
                onChange={(item) =>
                  onTimeFilterChange(item.value as TimeFilterValue)
                }
                defaultItem={selectedTimeFilterItem}
                fullWidth={false}
              />
            </div>
          </div>
          <AdvancedSearch
            config={searchConfig}
            onChange={onFiltersChange}
            restoredValues={filters}
          />
          <LoadingBlock loading={currentLoading}>
            <StyledTransactionsTable
              type={providerType}
              data={currentTransactionsData}
              pagination={{
                totalDocs: currentTotalDocs,
                page: currentPage,
                limit: currentLimit,
                onPaginationChange: currentOnPaginationChange,
              }}
            />
          </LoadingBlock>
        </TabsContainer>
      </MainContainer>
    </AnimateAppearanceWrapper>
  );
};

const tabsConfig = [
  {
    name: "All transactions",
    value: undefined,
  },
  {
    name: "Incoming",
    value: "incoming",
  },
  {
    name: "Outgoing",
    value: "outgoing",
  },
];
